import React, { useEffect, useRef, useState } from "react";

import { ButtonColorTypes, ButtonTypes } from "../shared-components/Button";
import Button from "../shared-components/Button";
import {
  AdditionalInfoData,
  BasicInfoData,
  ChecktypeData,
  DocumentUploadData,
  PersonInfoData,
  REPORT_WIZARD_STEPS,
  useReportsWizard,
} from "./ReportsWizardProvider";
import Container from "../shared-components/Container";
import {
  buildHybridRerunPersonPayload,
  personInfoApiFromWizardSteps,
} from "../api/hybridRerunPerson";
import {
  buildHybridRerunReportBody,
  usePostCreateReport2025,
  useRerunReportRequest,
} from "../api/ReportsApi";
import { useFamaFlags } from "../api/FamaFlags";
import {
  AddressesState,
  CheckTypeInputApi2025,
  CreateReportInputV2025Api,
  DegreesState,
  JobState,
  REPORT_TYPES,
} from "../api/Reports";
import { classNames, formatDateForEndpoint } from "../utils";
import { collectNormalizedProfileUrls } from "../utils/personFieldLimits";
import { eligibleReportFlags } from "./ReportsWizardUtil";
import { HARDCODED_ACTOR_UUID } from "../api/helpers";
import { NameType } from "../api/Person";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import LabeledCheckbox from "../shared-components/LabeledCheckbox";
import { useGetPersonDocuments } from "../api/360FilesAPI";
import { PersonDocument } from "../api/360Files";
import { PaperClipIcon } from "@heroicons/react/24/outline";

type Props = {};

export const ReportsWizardSummary = (props: Props) => {
  const {
    steps,
    companyQuery,
    companyWithBlueprint,
    blueprintsQuery,
    behaviorKit,
    personUUID,
    parentReportUUID,
    previousStep,
    is_rerun,
  } = useReportsWizard();

  const { hybridReportRerun, showBehaviorKit } = useFamaFlags();
  const navigate = useNavigate();
  const {
    mutateAsync: createPerson,
    isLoading: isLoadingCreatePerson,
    error: errorCreatePerson,
    errorData: errorCreatePersonData,
  } = usePostCreateReport2025();
  const {
    mutateAsync: rerunReportRequest,
    isLoading: isLoadingRerun,
    error: errorRerun,
  } = useRerunReportRequest();

  const isSubmitting = isLoadingCreatePerson || isLoadingRerun;
  const [clientProfileError, setClientProfileError] = useState("");

  const basicInfo = steps[REPORT_WIZARD_STEPS.BASIC_INFO].data as BasicInfoData;
  const personInfo = steps[REPORT_WIZARD_STEPS.PERSON_INFO]
    .data as PersonInfoData;
  const checktypeInfo = steps[REPORT_WIZARD_STEPS.CHECKTYPES]
    .data as ChecktypeData;
  const additionalInfo = steps[REPORT_WIZARD_STEPS.ADDITIONAL_INFO]
    .data as AdditionalInfoData;
  const documentUpload = steps[REPORT_WIZARD_STEPS.DOCUMENT_UPLOAD]
    .data as DocumentUploadData;

  const { mutateAsync: getPersonDocs, data: personDocs } =
    useGetPersonDocuments();
  useEffect(() => {
    if (personUUID) {
      getPersonDocs({ personUuid: personUUID });
    }
  }, [personUUID, getPersonDocs]);

  const resumeDocs: PersonDocument[] = (personDocs ?? []).filter(
    (doc) => doc.type === "resume"
  );
  const consentDocs: PersonDocument[] = (personDocs ?? []).filter(
    (doc) => doc.type === "consent-form"
  );
  const otherDocs: PersonDocument[] = (personDocs ?? []).filter(
    (doc) => doc.type === "other"
  );

  const anyDocs: boolean =
    documentUpload.writingSample.length > 0 ||
    resumeDocs.length > 0 ||
    consentDocs.length > 0 ||
    otherDocs.length > 0;

  const [verifyRecurChecked, setVerifyRecurChecked] = useState<boolean>(false);
  const [isValid, setIsValid] = useState(true);
  const isSubmittingRef = useRef(false);
  const verifyRecurText =
    "By checking this box, you are signing up for an Employee Screening Subscription for this individual and agree to be billed for each screening at your contracted rates. Contact your Fama Account Manager for subscription prices.";

  useEffect(() => {
    if (verifyRecurChecked) {
      setIsValid(true);
    }
  }, [verifyRecurChecked, setIsValid]);

  const reportFlags = eligibleReportFlags(checktypeInfo, companyWithBlueprint);

  const handleNextStep = () => {
    if (reportFlags.includeRecur && !verifyRecurChecked) {
      setIsValid(false);
      return;
    }
    // Sync guard: `isSubmitting` only disables the button after a re-render,
    // so it cannot block a second click fired in the same tick.
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;

    const profilesResult = collectNormalizedProfileUrls(personInfo.urls);
    if (!profilesResult.ok) {
      setClientProfileError(profilesResult.message);
      isSubmittingRef.current = false;
      return;
    }
    setClientProfileError("");
    const normalizedProfiles = profilesResult.urls;

    const hybridBlueprintUuid =
      checktypeInfo.selectedMisconductChecktype ||
      checktypeInfo.selectedThreeSixtyChecktype;

    if (
      hybridReportRerun &&
      is_rerun &&
      parentReportUUID &&
      hybridBlueprintUuid
    ) {
      const selectedBlueprintMeta =
        companyWithBlueprint?.blueprintsInfo?.find(
          (b) => b.blueprint_uuid === hybridBlueprintUuid
        ) ??
        blueprintsQuery.data?.find(
          (b) => b.blueprint_uuid === hybridBlueprintUuid
        );
      const includeBehaviorKitInPayload =
        showBehaviorKit &&
        selectedBlueprintMeta?.report_type === REPORT_TYPES.MISCONDUCT;

      const currentPerson = personInfoApiFromWizardSteps({
        firstName: basicInfo.firstName,
        middleName: basicInfo.middleName,
        lastName: basicInfo.lastName,
        aliases: personInfo.aliases,
        emails: personInfo.emails,
        phones: personInfo.phones,
        dateOfBirth: personInfo.dateOfBirth,
        jobs: additionalInfo.jobs,
        addresses: additionalInfo.addresses,
        degrees: additionalInfo.degrees,
      });
      const person = buildHybridRerunPersonPayload({
        current: currentPerson,
        modifiedBy: HARDCODED_ACTOR_UUID,
        personUuid: personUUID ?? undefined,
        parentPersonUuid: personInfo.personUuid,
      });

      rerunReportRequest({
        reportUUID: parentReportUUID,
        body: buildHybridRerunReportBody({
          blueprintUuid: hybridBlueprintUuid,
          behaviorKitUuid: checktypeInfo.selectedBehaviorKit,
          includeBehaviorKitInPayload,
          profileUrls: normalizedProfiles,
          person,
          customId: basicInfo.clientReportId,
        }),
      })
        .then(() => {
          navigate("/reports", { state: { reportSubmitted: true } });
        })
        .catch(() => {
          // error surfaced via errorRerun -> errorMessage below
        })
        .finally(() => {
          isSubmittingRef.current = false;
        });
      return;
    }

    const names = [
      {
        first_name: basicInfo.firstName.trim(),
        last_name: basicInfo.lastName.trim(),
        middle_name: basicInfo.middleName.trim(),
        name_type: NameType.MAIN,
      },
      ...personInfo.aliases.map((name) => {
        return {
          first_name: name.firstName.trim(),
          last_name: name.lastName.trim(),
          middle_name: name.middleName.trim(),
          name_type: NameType.ALIAS,
        };
      }),
    ];

    let checkTypes: CheckTypeInputApi2025[] = [];

    if (checktypeInfo.selectedMisconductChecktype) {
      let checkType: CheckTypeInputApi2025 = {
        check_type_uuid: checktypeInfo.selectedMisconductChecktype,
        behavior_kit_uuid:
          checktypeInfo.selectedBehaviorKit === "NULL" ||
          checktypeInfo.selectedBehaviorKit === ""
            ? undefined
            : checktypeInfo.selectedBehaviorKit,
        custom_fields: {
          custom_id: basicInfo.clientReportId,
          job_title: basicInfo.jobTitle ?? "None",
          assessment_id: basicInfo.clientReportId,
        },
      };

      checkTypes.push(checkType);
    }

    if (checktypeInfo.selectedThreeSixtyChecktype) {
      let checkType: CheckTypeInputApi2025 = {
        check_type_uuid: checktypeInfo.selectedThreeSixtyChecktype,
        behavior_kit_uuid:
          checktypeInfo.selectedBehaviorKit === "NULL" ||
          checktypeInfo.selectedBehaviorKit === ""
            ? undefined
            : checktypeInfo.selectedBehaviorKit,
        custom_fields: {
          custom_id: basicInfo.clientReportId,
        },
      };
      checkTypes.push(checkType);
    }

    const jobs: JobState[] = additionalInfo.jobs.map((job) => ({
      title: job.title,
      employer: job.company,
      country: job.country ?? "",
      start_date: job.startDate ?? undefined,
      end_date: job.endDate ?? undefined,
    }));
    const addresses: AddressesState[] = additionalInfo.addresses.map(
      (address) => ({
        line1: address.street,
        line2: "",
        city: address.city,
        state: address.state,
        zip: address.zip,
        country: address.country,
      })
    );
    const degrees: DegreesState[] = additionalInfo.degrees.map((degree) => ({
      name: degree.degree,
      educator: degree.institution,
      country: degree.country ?? "",
      start_date: degree.startDate ?? undefined,
      end_date: degree.endDate ?? undefined,
    }));

    const createPersonPayload: CreateReportInputV2025Api = {
      person: {
        person_uuid: !personUUID ? undefined : personUUID,
        names: names,
        jobs,
        addresses,
        birth_date: formatDateForEndpoint(personInfo.dateOfBirth),
        degrees,
        phones: personInfo.phones.map((p) => ({
          raw_number: p,
        })),
        emails: personInfo.emails.map((email) => ({
          address: email,
        })),
        // WARNING!!! TD-1: remove this property once the Authorization cicle gets completed
        modified_by: HARDCODED_ACTOR_UUID,
      },
      profiles: normalizedProfiles,
      company_uuid: basicInfo.companyUUID,
      check_types: checkTypes,
      cqa: reportFlags.cqa,
      iqa: reportFlags.iqa,
    };
    if (reportFlags.includeRecur) {
      createPersonPayload.recur = {
        schedule_type: "interval",
        schedule_value: `${checktypeInfo.pulseReportMonths}`,
      };
    }

    if (parentReportUUID) {
      createPersonPayload.rerun_from_parent_uuid = parentReportUUID;
    }

    createPerson(createPersonPayload)
      .then((resultCreatePerson) => {
        if (resultCreatePerson) {
          navigate("/reports", { state: { reportSubmitted: true } });
          // the state: reportSubmitted is for the toast message in the reports lists...
        }
      })
      .catch(() => {
        // error surfaced via errorCreatePerson -> errorMessage below
      })
      .finally(() => {
        isSubmittingRef.current = false;
      });
  };

  const company = companyQuery.data?.find(
    (company) => company.external_id === basicInfo?.companyUUID
  );
  let errorMessage = clientProfileError
    ? [
        `Couldn't save the report please check the field information and try again.`,
        clientProfileError,
      ].map((p) => <div>{p}</div>)
    : errorCreatePerson
    ? [
        `Couldn't save the report please check the field information and try again.`,
        ...errorCreatePersonData,
      ].map((p) => <div>{p}</div>)
    : errorRerun
    ? [`Couldn't rerun the report. Please try again or contact support.`].map(
        (p) => <div>{p}</div>
      )
    : "";

  const misconductCheckTypeName = companyWithBlueprint?.blueprintsInfo.find(
    (blueprint) =>
      blueprint.blueprint_uuid === checktypeInfo.selectedMisconductChecktype
  )?.name;
  const threeSixtyCheckTypeName = companyWithBlueprint?.blueprintsInfo.find(
    (blueprint) =>
      blueprint.blueprint_uuid === checktypeInfo.selectedThreeSixtyChecktype
  )?.name;
  const behaviorKitName =
    behaviorKit?.find(
      (behaviorKit) =>
        behaviorKit.external_id === checktypeInfo.selectedBehaviorKit
    )?.name ?? "General";

  const errorGettingCheckTypeName =
    (misconductCheckTypeName === "" || misconductCheckTypeName === undefined) &&
    (threeSixtyCheckTypeName === "" || threeSixtyCheckTypeName === undefined)
      ? "Couldn't get the Checktype, please navigate back to the checktype selection step and pick a valid one."
      : "";

  return (
    <Container className="py-8 px-16 text-gray-500">
      <Title className="mb-8">Person Info</Title>
      <div className="p-4 border border-gray-200 rounded-lg mt-4 bg-gray-100">
        <div className="flex gap-2">
          <div className="flex flex-col gap-2 flex-1">
            <SubTitle>First Name</SubTitle>
            <span>{basicInfo.firstName}</span>
          </div>
          <div className="flex flex-col gap-2 flex-1">
            <SubTitle>Middle Name</SubTitle>
            <span>{basicInfo.middleName}</span>
          </div>
          <div className="flex flex-col gap-2 flex-1">
            <SubTitle>Last Name</SubTitle>
            <span>{basicInfo.lastName}</span>
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <div className="flex flex-col gap-2 flex-1">
            <SubTitle>Company</SubTitle>
            <span>{company?.name ?? "-"}</span>
          </div>
          <div className="flex flex-col gap-2 flex-1">
            <SubTitle>Job Title</SubTitle>
            <span>{basicInfo.jobTitle ?? "-"}</span>
          </div>
          <div className="flex flex-col gap-2 flex-1">
            <SubTitle>Client Report ID</SubTitle>
            <span>{basicInfo.clientReportId ?? "-"}</span>
          </div>
        </div>
      </div>

      <div className="p-4 border border-gray-200 rounded-lg mt-4 bg-gray-100">
        {personInfo.aliases.map((alias) => (
          <div className="flex gap-2">
            <>
              <div className="flex flex-col gap-2 flex-1">
                <SubTitle>Aliases First Name</SubTitle>
                <span>{alias.firstName}</span>
              </div>
              <div className="flex flex-col gap-2 flex-1">
                <SubTitle>Aliases Middle Name</SubTitle>
                <span>{alias.middleName}</span>
              </div>
              <div className="flex flex-col gap-2 flex-1">
                <SubTitle>Aliases Last Name</SubTitle>
                <span>{alias.lastName}</span>
              </div>
            </>
          </div>
        ))}
        <div className="flex gap-2 mt-4">
          <div className="flex flex-col gap-2 flex-1">
            <SubTitle>Email</SubTitle>
            {personInfo.emails.map((email) => (
              <span>{email}</span>
            ))}
          </div>
          <div className="flex flex-col gap-2 flex-1">
            <SubTitle>Phone Number</SubTitle>
            {personInfo.phones.map((phoneNumber) => (
              <span>{phoneNumber ?? "-"}</span>
            ))}
          </div>
          <div className="flex flex-col gap-2 flex-1">
            <SubTitle>Date of Birth</SubTitle>
            <span>{personInfo.dateOfBirth ?? "-"}</span>
          </div>
        </div>
      </div>
      {personInfo.urls.length > 0 && (
        <div className="p-4 border border-gray-200 rounded-lg mt-4 bg-gray-100 flex flex-col gap-4">
          <SubTitle>Profile URL:</SubTitle>
          {personInfo.urls.map((url) => (
            <span>{url}</span>
          ))}
        </div>
      )}

      <Title className="mt-8 mb-8">Check Types</Title>
      <div className="p-4 border border-gray-200 rounded-lg mt-4 bg-gray-100">
        {errorGettingCheckTypeName && (
          <div className="p-4 border border-gray-200 rounded-lg mt-4 bg-red-500 text-white">
            {errorGettingCheckTypeName}
          </div>
        )}
        <div className="flex gap-2">
          {checktypeInfo.selectedMisconductChecktype && (
            <div className="flex flex-col gap-2 flex-1">
              <SubTitle>Misconduct Check Types</SubTitle>
              <span>{misconductCheckTypeName}</span>
            </div>
          )}
          {checktypeInfo.selectedThreeSixtyChecktype && (
            <div className="flex flex-col gap-2 flex-1">
              <SubTitle>360 Check Types</SubTitle>
              <span>{threeSixtyCheckTypeName}</span>
            </div>
          )}
          <div className="flex flex-col gap-2 flex-1"></div>
        </div>
        <div className="flex gap-2 mt-4">
          <div className="flex flex-col gap-2 flex-1">
            <SubTitle>Behavior Kit</SubTitle>
            <span>{behaviorKitName}</span>
          </div>
          {(reportFlags.cqa || reportFlags.iqa) && (
            <div className="flex flex-col gap-2 flex-1">
              <SubTitle>Investigator</SubTitle>
              {reportFlags.cqa ? <span>Client Investigator</span> : ""}
              {reportFlags.iqa ? <span>Internal Investigator</span> : ""}
            </div>
          )}
          <div className="flex flex-col gap-2 flex-1"></div>
          {!(reportFlags.cqa || reportFlags.iqa) && (
            <div className="flex flex-col gap-2 flex-1"></div>
          )}
        </div>
      </div>
      {reportFlags.includeRecur && (
        <div className="p-4 border border-gray-200 rounded-lg mt-4 bg-gray-100 flex flex-col gap-2">
          <SubTitle>Employee Screening Subscription</SubTitle>
          <span>
            Employee Rescreening Report, Every {checktypeInfo.pulseReportMonths}{" "}
            Months
          </span>
          <LabeledCheckbox
            name="verify-recur"
            label={verifyRecurText}
            defaultChecked={verifyRecurChecked}
            alignCheckTop={true}
            preserveLabelCasing={true}
            classes={
              "bg-white border-2 rounded-lg m-1 mt-5 p-2 " +
              (isValid ? "border-our-blue" : "border-red-500")
            }
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setVerifyRecurChecked(e.target.checked);
            }}
          ></LabeledCheckbox>
          {!isValid && (
            <div className="text-red-500">Please select check box</div>
          )}
        </div>
      )}
      {(additionalInfo.addresses.length > 0 ||
        additionalInfo.degrees.length > 0) && (
        <Title className="mt-8 mb-8">Additional Info</Title>
      )}
      {additionalInfo.addresses.length > 0 && (
        <div className="p-4 border border-gray-200 rounded-lg mt-4 bg-gray-100 flex flex-col gap-2">
          <SubTitle>Address:</SubTitle>
          {additionalInfo.addresses.map((address) => (
            <div className="flex gap-2 mt-4">
              <div className="flex flex-col gap-2 flex-1">
                <SubTitle>Street Address</SubTitle>
                <span>{address.street}</span>
              </div>
              <div className="flex flex-col gap-2 flex-1">
                <SubTitle>City/Village</SubTitle>
                <span>{address.city}</span>
              </div>
              <div className="flex flex-col gap-2 flex-1">
                <SubTitle>State/Province</SubTitle>
                <span>{address.state}</span>
              </div>
              <div className="flex flex-col gap-2 flex-1">
                <SubTitle>Postal Code</SubTitle>
                <span>{address.zip}</span>
              </div>
              <div className="flex flex-col gap-2 flex-1">
                <SubTitle>Country</SubTitle>
                <span>{address.country}</span>
              </div>
            </div>
          ))}
        </div>
      )}
      {additionalInfo.jobs.length > 0 && (
        <div className="p-4 border border-gray-200 rounded-lg mt-4 bg-gray-100 flex flex-col gap-2">
          <SubTitle>Employment:</SubTitle>
          {additionalInfo.jobs.map((job) => (
            <div className="flex gap-2 mt-4">
              <div className="flex flex-col gap-2 flex-1">
                <SubTitle>Job Title</SubTitle>
                <span>{job.title}</span>
              </div>
              <div className="flex flex-col gap-2 flex-1">
                <SubTitle>Company Name</SubTitle>
                <span>{job.company}</span>
              </div>
              <div className="flex flex-col gap-2 flex-1">
                <SubTitle>Start Date</SubTitle>
                <span>{job.startDate}</span>
              </div>
              <div className="flex flex-col gap-2 flex-1">
                <SubTitle>End Date</SubTitle>
                <span>{job.endDate}</span>
              </div>
              <div className="flex flex-col gap-2 flex-1">
                <SubTitle>Country</SubTitle>
                <span>{job.country}</span>
              </div>
            </div>
          ))}
        </div>
      )}
      {additionalInfo.degrees.length > 0 && (
        <div className="p-4 border border-gray-200 rounded-lg mt-4 bg-gray-100 flex flex-col gap-2">
          <SubTitle>Education:</SubTitle>
          {additionalInfo.degrees.map((degree) => (
            <div className="flex gap-2 mt-4">
              <div className="flex flex-col gap-2 flex-1">
                <SubTitle>Degree</SubTitle>
                <span>{degree.degree}</span>
              </div>
              <div className="flex flex-col gap-2 flex-1">
                <SubTitle>Institution</SubTitle>
                <span>{degree.institution}</span>
              </div>
              <div className="flex flex-col gap-2 flex-1">
                <SubTitle>Start Date</SubTitle>
                <span>{degree.startDate}</span>
              </div>
              <div className="flex flex-col gap-2 flex-1">
                <SubTitle>End Date</SubTitle>
                <span>{degree.endDate}</span>
              </div>
              <div className="flex flex-col gap-2 flex-1">
                <SubTitle>Country</SubTitle>
                <span>{degree.country}</span>
              </div>
            </div>
          ))}
        </div>
      )}
      {anyDocs && (
        <>
          <Title className="mt-8 mb-8">Uploaded Documents</Title>
          <div className="p-4 border border-gray-200 rounded-lg bg-gray-100 flex flex-col gap-2">
            {documentUpload.writingSample.length > 0 && (
              <div className="m-2">
                <SubTitle>Writing Sample:</SubTitle>
                <span>{documentUpload.writingSample}</span>
              </div>
            )}
            {resumeDocs.length > 0 && (
              <div className="m-2">
                <SubTitle>Resume</SubTitle>
                {resumeDocs.map((doc) => (
                  <div className="flex gap-5">
                    <PaperClipIcon className="w-5" />
                    {doc.filename}
                  </div>
                ))}
              </div>
            )}
            {consentDocs.length > 0 && (
              <div className="m-2">
                <SubTitle>Consent Form</SubTitle>
                {consentDocs.map((doc) => (
                  <div className="flex gap-5">
                    <PaperClipIcon className="w-5" />
                    {doc.filename}
                  </div>
                ))}
              </div>
            )}
            {otherDocs.length > 0 && (
              <div className="m-2">
                <SubTitle>Other Documents</SubTitle>
                {otherDocs.map((doc) => (
                  <div className="flex gap-5">
                    <PaperClipIcon className="w-5" />
                    {doc.filename}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
      {errorMessage && (
        <div className="p-4 border border-gray-200 rounded-lg my-4 bg-red-500 text-white">
          {errorMessage}
        </div>
      )}
      <div className="gray-500 text-sm mt-8">
        By clicking the "Submit" button below, you confirm that the provided
        information is accurate and complete.
      </div>
      <div className="flex justify-between pt-8">
        <Button
          type={ButtonTypes.default}
          text={"Previous Step"}
          colorType={ButtonColorTypes.alternate}
          onClick={() => previousStep()}
          disabled={isSubmitting}
        />
        {isSubmitting && <LoadingSpinner />}
        <Button
          text={"Submit"}
          colorType={ButtonColorTypes.custom}
          isRoundedFull={true}
          className="bg-orange-500 text-white rounded-full ml-auto w-56"
          onClick={handleNextStep}
          disabled={isSubmitting || !!errorGettingCheckTypeName || !isValid}
        />
      </div>
    </Container>
  );
};

function Title({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={classNames(
        className ?? "",
        "text-3xl font-bold text-darkBlue"
      )}
    >
      {children}
    </div>
  );
}

function SubTitle({ children }: { children: React.ReactNode }) {
  return <span className="font-bold text-darkBlue">{children}</span>;
}
